import { useState, useEffect, useMemo } from 'react'

import { MarkdownRenderer } from './MarkdownRenderer'

interface TypewriterTextProps {
  readonly text: string
  readonly speed?: number // milliseconds per character
  readonly onComplete?: () => void
  readonly className?: string
  readonly renderMarkdown?: boolean
  readonly maxDuration?: number // Maximum duration in milliseconds (for historical messages)
}

export function TypewriterText({ text, speed = 30, onComplete, className, renderMarkdown = false, maxDuration }: Readonly<TypewriterTextProps>) {
  const [displayedText, setDisplayedText] = useState('')
  const [currentIndex, setCurrentIndex] = useState(0)

  // Calculate actual speed based on maxDuration if provided (for historical messages)
  // If maxDuration is set, calculate speed to complete in maxDuration (max 5 seconds)
  const actualSpeed = useMemo(() => {
    if (maxDuration && text.length > 0) {
      const calculatedSpeed = Math.max(1, Math.floor(maxDuration / text.length))
      // Use the faster of the two (calculated or provided speed)
      return Math.min(calculatedSpeed, speed)
    }
    return speed
  }, [maxDuration, text.length, speed])

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText((prev) => prev + text[currentIndex])
        setCurrentIndex((prev) => prev + 1)
      }, actualSpeed)

      return () => clearTimeout(timeout)
    } else if (currentIndex === text.length && onComplete) {
      onComplete()
    }
  }, [currentIndex, text, actualSpeed, onComplete])

  // Reset when text changes
  useEffect(() => {
    setDisplayedText('')
    setCurrentIndex(0)
  }, [text])

  if (renderMarkdown) {
    return <MarkdownRenderer content={displayedText} className={className} />
  }

  return <span className={className}>{displayedText}</span>
}

